import { GlobalToast } from '@/app';
import { renderToastActionContent } from '@/ui/toast/ToastActionContent';
import {
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from '@/utils/helpers/icons.helper';
import { createElement, useContext } from 'react';

export type ToastType = (
  summary: string,
  detail: string,
  life?: number | undefined,
) => void;

export type MultipleToastType = {
  severity: 'success' | 'warn' | 'info' | 'error';
  summary: string;
  detail: string;
  life?: number;
};

export type ActionToastOptions = {
  severity: 'success' | 'warn' | 'info' | 'error';
  summary: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
  closable?: boolean;
};

const getIconBySeverity = (
  severity: 'success' | 'warn' | 'info' | 'error',
): JSX.Element => {
  return createElement(
    {
      success: SuccessIcon,
      info: InfoIcon,
      warn: WarningIcon,
      error: ErrorIcon,
    }[severity],
  );
};

export const useToast = () => {
  const toast = useContext<any>(GlobalToast);

  const baseToast = (
    severity: 'success' | 'warn' | 'info' | 'error',
    summary: string,
    detail: string,
    life?: number,
  ) => {
    const content = ({ onClose }: { onClose?: () => void }) =>
      renderToastActionContent({
        icon: getIconBySeverity(severity),
        summary,
        detail,
        onClose,
      });

    const isError = severity === 'error';
    toast.current.show({
      severity,
      content,
      ...(isError ? { sticky: true } : { life: life ?? 3000 }),
      className: 'toast-v2',
    });
  };

  const multipleToasts = (toasts: MultipleToastType[]) =>
    toast.current.show(
      toasts.map(({ severity, summary, detail, life }) => {
        const isError = severity === 'error';
        return {
          severity,
          ...(isError ? { sticky: true } : { life: life ?? 3000 }),
          className: 'toast-v2',
          content: ({ onClose }: { onClose?: () => void }) =>
            renderToastActionContent({
              icon: getIconBySeverity(severity),
              summary,
              detail,
              onClose,
            }),
        };
      }),
    );

  const actionToast = ({
    severity,
    summary,
    detail,
    actionLabel,
    onAction,
    closable = true,
  }: ActionToastOptions) => {
    const content = ({ onClose }: { onClose?: () => void }) =>
      renderToastActionContent({
        icon: getIconBySeverity(severity),
        summary,
        detail,
        actionLabel,
        onAction,
        onClose,
      });

    toast.current.show({
      severity,
      sticky: true,
      closable,
      content,
      className: 'toast-v2',
    });
  };

  return toast?.current
    ? {
        success: baseToast.bind(this, 'success'),
        warning: baseToast.bind(this, 'warn'),
        info: baseToast.bind(this, 'info'),
        error: baseToast.bind(this, 'error'),
        multiple: multipleToasts,
        action: actionToast,
      }
    : null;
};
